<template>
  <div v-if="planSale">
    <!-- Page Header -->
    <ShowPageHeader :backLabel="$t('Plan sales')" :backRoute="backRoute" :title="planSale.friendlyId">
      <template #badges>
        <FieldSpan :value="planSale.category" style-to-add="bg-blue-100 text-blue-800" />
        <FieldSpan :value="planSale.status" :style-to-add="planSaleStatusClass" translation-prefix="planSale.status" />
        <FieldSpan
          v-if="planSale.compliant === true"
          :value="t('compliant')"
          style-to-add="text-green-500 bg-green-100 border border-green-300"
        />
        <FieldSpan
          v-if="planSale.compliant === false"
          :value="t('non_compliant')"
          style-to-add="text-red-500 bg-red-100 border border-red-300"
        />
      </template>
      <template #actions v-if="planSaleActive">
        <div v-if="planSaleCancelAllowed">
          <SecondaryButton @click="openCancelModal = true">❌ Résilier</SecondaryButton>
          <CancellationModal
            :open="openCancelModal"
            :plan-sale="planSale"
            @close="openCancelModal = false"
            @cancelPlanSale="cancelPlanSale"
          />
        </div>

        <div>
          <SecondaryButton @click="openEditModal = true">✏️ Modifier</SecondaryButton>
          <UpdateModal
            :open="openEditModal"
            :planSale="planSale"
            :planSaleCompleteUpdateAllowed="planSaleCompleteUpdateAllowed"
            @closeUpdateModal="openEditModal = false"
            @updatePlanSale="updatePlanSale"
          />
        </div>

        <div v-if="isProductExchangeAllowed">
          <SecondaryButton @click="productExchangeFormModalOpen = true">🔄 Échanger le produit</SecondaryButton>
          <ProductExchangeFormModal
            :open="productExchangeFormModalOpen"
            :plan="planSale"
            @close="productExchangeFormModalOpen = false"
            @refreshPlanSale="refreshPlanSale"
          />
        </div>

        <SecondaryButton @click="sendConfirmationEmail">✉️ Envoyer le mail de confirmation</SecondaryButton>
      </template>
    </ShowPageHeader>

    <!-- Plan Sale Summary Card -->
    <div class="mt-6">
      <div class="bg-white rounded-2xl shadow-md p-6 mx-auto">
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2">
          <!-- Column 1 - Produit -->
          <div class="space-y-2">
            <StackedField label="Formule" :value="planSale.title" />
            <StackedField label="Prix" :value="planSalePrice" />
            <StackedField label="Paiement" :value="planSale.monthly ? 'Mensuel' : 'Annuel'" />
            <StackedField
              v-if="planSale.orderReferenceId"
              label="Commande marchand"
              :value="planSale.orderReferenceId"
            />
          </div>

          <!-- Column 2 - Commercial -->
          <div class="space-y-2">
            <StackedField label="Offre" :value="planSale.category" />
            <StackedField label="Boutique" :value="planSale.store?.salesChannel" />
            <StackedField v-if="sellerName" label="Vendeur" :value="sellerName" />
          </div>

          <!-- Column 3 - Contrat & Dates -->
          <div class="space-y-2">
            <StackedField label="Début du contrat" :value="planSale.date" />
            <StackedField
              v-if="planSale?.cancellation?.date"
              label="Fin du contrat"
              :value="planSale.cancellation.date"
            />
            <StackedField label="Fin du contrat théorique" :value="planSale.expirationDate" />
            <StackedField label="Engagement" :value="cancellationPolicyTypeLabel" />
          </div>
        </div>

        <!-- Membership Agreement Download -->
        <div v-if="planSale.membershipAgreement" class="mt-6 pt-4 border-t border-gray-200">
          <LinkField label="Certificat d'adhésion" :href="planSale.membershipAgreement" />
        </div>
      </div>
    </div>

    <!-- Tabs Navigation -->
    <TabsNavigation :tabs="tabs" class="my-6" />

    <div v-if="currentTab === 'products'" class="mt-6">
      <InsuredProductsInformations :products="planSale.products" />
    </div>

    <div v-if="currentTab === 'customer'" class="mt-6">
      <CustomerInformations :customer="planSale.customer" />
    </div>

    <div v-if="currentTab === 'payments'" class="mt-6">
      <PlanSaleBillingDetails :planSaleBillings="planSale.planSaleBillings" :chargedBy="planSale.chargedBy" />
    </div>

    <div v-if="currentTab === 'retributions'" class="mt-6">
      <RetributionDetails :retribution="retribution" />
    </div>
  </div>
</template>

<script>
// Vue components
import UpdateModal from "@/views/admin/plansale/details/UpdateModal.vue";
import CancellationModal from "@/views/admin/plansale/details/CancellationModal.vue";
import CustomerInformations from "@/components/plansales/CustomerInformations.vue";
import InsuredProductsInformations from "@/components/plansales/InsuredProductsInformations.vue";
import PlanSaleBillingDetails from "@/components/plansales/PlanSaleBillingDetails.vue";
import RetributionDetails from "@/components/plansales/RetributionDetails.vue";

// UI components and utilities
import {
  SecondaryButton,
  StackedField,
  FieldSpan,
  LinkField,
  ShowPageHeader,
  TabsNavigation,
  formatPrice,
  isPurchasedToday,
} from "@estaly/ui";
import { isAdminRoute } from "@estaly/ui/src/utils/route";

// i18n
import { useI18n } from "vue-i18n";

// Vuex
import { mapActions, mapMutations, mapGetters } from "vuex";

// API services
import PlanSalesService from "@/api/services/planSales";
import ProductExchangeFormModal from "@/views/common/plan_sales/ProductExchangeFormModal.vue";

export default {
  name: "PlanSaleDetails",

  setup() {
    const { t } = useI18n();
    return { t };
  },

  components: {
    ProductExchangeFormModal,
    // Modals
    CancellationModal,
    UpdateModal,

    // UI components
    SecondaryButton,
    StackedField,
    FieldSpan,
    LinkField,
    ShowPageHeader,
    TabsNavigation,

    // Detail components
    CustomerInformations,
    InsuredProductsInformations,
    PlanSaleBillingDetails,
    RetributionDetails,
  },

  data() {
    return {
      // Plan sale data
      planSale: null,
      planSaleId: this.$route.params.id,

      // UI state
      openEditModal: false,
      openCancelModal: false,
      productExchangeFormModalOpen: false,
    };
  },

  computed: {
    planSalePrice() {
      return this.planSale.monthly ? formatPrice(this.planSale.price) + " / mois" : formatPrice(this.planSale.price);
    },

    currentTab() {
      return this.$route.query.tab || "customer";
    },

    // Vuex getters
    ...mapGetters("store", ["isLoading", "selectedStoreId", "hasPlanSaleUpdatePermission"]),
    ...mapGetters("auth", ["isOrganizationOrStoreAdmin", "isManager", "isResponsible"]),
    ...mapGetters("planSales", ["productExchangeAllowed", "isActive", "statusBadgeClass"]),

    backRoute() {
      return this.isAdminRoute() ? "/admin/plan-sales" : "/pos/plan-sales";
    },

    tabs() {
      return [
        { name: "customer", displayName: "Client", query: { tab: "customer" } },
        { name: "products", displayName: "Produits", query: { tab: "products" } },
        { name: "retributions", displayName: "Primes", query: { tab: "retributions" } },
        { name: "payments", displayName: "Paiements", query: { tab: "payments" } },
      ];
    },

    sellerName() {
      return [this.planSale.seller?.firstName, this.planSale.seller?.lastName].filter(Boolean).join(" ");
    },

    planSaleActive() {
      return this.isActive(this.planSale);
    },

    planSaleCancelAllowed() {
      if (isPurchasedToday(this.planSale?.purchaseDate)) return true;
      return this.planSale.cancellationAvailableForCustomer && this.planSaleCompleteUpdateAllowed;
    },

    planSaleCompleteUpdateAllowed() {
      return (
        this.hasPlanSaleUpdatePermission(this.planSale.storeId) &&
        (this.isResponsible || this.isOrganizationOrStoreAdmin || this.isManager)
      );
    },

    planSaleStatusClass() {
      return this.statusBadgeClass(this.planSale);
    },

    retribution() {
      return this.planSale?.planSaleBillings?.[0]?.retribution;
    },

    isProductExchangeAllowed() {
      return this.productExchangeAllowed(this.planSale, this.planSale?.claims);
    },

    cancellationPolicyTypeLabel() {
      const type = this.planSale?.cancellationPolicyType;
      if (!type) return null;

      return this.$t(`planSale.cancellationPolicyType.${type}`);
    },
  },

  async mounted() {
    await this.getPlanSale();
  },

  methods: {
    // Vuex actions and mutations
    ...mapActions("notifications", ["notify"]),
    ...mapActions("planSales", ["fetch"]),
    ...mapMutations("store", ["setIsLoading"]),
    isAdminRoute,

    // Data fetching
    async getPlanSale() {
      const includes = [
        "seller",
        "cancellation",
        "plan_sale_billings",
        "store",
        "contract_distribution",
        "customer",
        "plan_sale_billings.commissions",
        "plan_sale_billings.retribution",
        "plan_sale_product",
        "membership_agreement",
        "claims",
      ];

      this.planSale = await this.fetch({ id: this.planSaleId, includes });
    },

    async refreshPlanSale() {
      await this.getPlanSale();
    },

    // Plan sale actions
    async cancelPlanSale(reason, message) {
      this.setIsLoading(true);

      const formData = {
        planSale: {
          cancellationReason: reason,
          feedbackMessage: message,
        },
      };

      let response;
      if (this.isAdminRoute()) {
        response = await PlanSalesService.cancel(this.planSaleId, formData);
      } else {
        response = await PlanSalesService.cancelForStore(this.planSaleId, this.selectedStoreId, formData);
      }

      if (response?.success) {
        this.openCancelModal = false;
        await this.getPlanSale();
        await this.notify({
          category: "simple",
          type: "success",
          text: "Contract cancelled",
        });
      }

      this.setIsLoading(false);
    },

    async updatePlanSale(planSaleInfo) {
      this.setIsLoading(true);

      const formData = { planSale: planSaleInfo };

      let response;
      if (this.isAdminRoute()) {
        response = await PlanSalesService.update(this.planSaleId, formData);
      } else {
        response = await PlanSalesService.updateForStore(this.planSaleId, this.selectedStoreId, formData);
      }

      if (response?.success) {
        this.openEditModal = false;
        await this.getPlanSale();
        await this.notify({
          category: "simple",
          type: "success",
          text: "Contract updated",
        });
      }

      this.setIsLoading(false);
    },

    async sendConfirmationEmail() {
      if (!confirm("Envoyer le mail de confirmation de souscription ?")) return;

      const { success } = await PlanSalesService.sendConfirmationEmail(this.planSale.friendlyId);
      if (!success) return;

      await this.notify({
        category: "simple",
        type: "success",
        title: "Email envoyé",
      });
    },
  },
};
</script>

<i18n src="./PlanSaleDetails.i18n.json"></i18n>
